'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

import FodmapEntryModal from './FodmapEntryModal.react';
import Select from '../../../../pro/components/Widgets/Select.react';

import allNutrients from '../../../../tables/nutrients';
import allRecommendations from '../../../../tables/recommendations';
import nutrientGroups from '../../../../tables/nutrient-groups';
import { scrollTextFieldIntoView } from '../../../../utils/Keyboard';
import TooltipText from '../../../Widgets/Common/TooltipText.react';

import './NutrientEditor.scss';

const dailyValues = allRecommendations['all-ages'];


// dv stands for 'daily value'
const percentNutrients = [
];

export default class NutrientEditor extends Component {

    static propTypes = {
        values: PropTypes.object,
        available: PropTypes.object,
        isRecipeEditor: PropTypes.bool,
        onChangeServingSizeUnit: PropTypes.func,
        onShowMore: PropTypes.func,
        unitOpts: PropTypes.object,

        allowedNutrNos: PropTypes.array,
    };

    static defaultProps = {
        isRecipeEditor: false,
        onChangeServingSizeUnit: null,
        unitOpts: null,
        onShowMore: null,
    };

    constructor(props) {
        super(props);

        this.state = {
            ...this.getStateFromProps(props),
            errored: {},
        };

        this.resetInhibitUpdate = debounce(this.resetInhibitUpdate, 250);
    }

    inhibitUpdate = false;

    resetInhibitUpdate = () => {
        this.inhibitUpdate = false;
    }

    UNSAFE_componentWillReceiveProps = (nextProps, nextContext) => {
        if (!this.inhibitUpdate) {
            this.setState(this.getStateFromProps(nextProps, this.state.modes));
        }
    }

    getStateFromProps = ({values, available = {}, allowedNutrNos}, modes = {}) => {
        let nutrients = JSON.parse(JSON.stringify(values || {}));

        nutrientGroups.forEach(group => {
            group.nutrNos.forEach(def => {
                if (allowedNutrNos && !allowedNutrNos.includes(def.nutrNo)) {
                    return;
                }

                let value = available[def.nutrNo] ? values[def.nutrNo] : '';

                // If this nutrient is in percentage mode, convert the incoming value to a percentage of daily values
                if (modes[def.nutrNo] === '%') {
                    value = (value / def.dv * 100);
                }

                nutrients[def.nutrNo] = value;
            });
        });

        return {
            values: nutrients,
            available,
            modes,
        };
    }

    updateParent = () => {
        const { modes, available } = this.state;
        const { onChange } = this.props;

        const values = {};

        Object.keys(this.state.values).forEach(nutrNo => {
            const parsed = parseFloat(this.state.values[nutrNo]);

            if (typeof parsed !== 'number' || isNaN(parsed)) {
                available[nutrNo] = false;
                return;
            }

            values[nutrNo] = (modes[nutrNo] === '%')
                           ? (parsed / 100 * dailyValues[nutrNo])
                           : parsed;
        });

        this.inhibitUpdate = true;

        onChange({values, available});

        this.resetInhibitUpdate();
    }

    onNutrientValueChange = (nutrNo, value) => {
        const { values, errored, available, modes } = this.state;
        values[nutrNo] = value;

        if (value.length == 0) {
            available[nutrNo] = false;
        } else {
            available[nutrNo] = true;
            const parsed = value.length > 0 ? parseFloat(value) : 0;
            if (typeof parsed !== 'number' || isNaN(parsed)) {
                errored[nutrNo] = true;
            } else {
                delete errored[nutrNo];
            }
        }

        // if fiber is set to zero, set soluable and insoluable fiber to zero too
        if (nutrNo === '291' && value == 0 && available['291']) {
            values['SFB'] = 0;
            values['IFB'] = 0;
            available['SFB'] = available['IFB'] = true;
        }

        // If total sugar is set to zero, set added sugar to zero too
        if (nutrNo === '269' && value == 0 && available['269']) {
            values['ASG'] = 0;
            available['ASG'] = true;
        }

        // If the user entered 0 total fat, then a bunch of fatty values are also zero.
        if (nutrNo === '204' && value == 0 && available['204']) {
            values['606'] = 0; available['606'] = true; // Saturated Fat (g)
            values['605'] = 0; available['605'] = true; // Trans Fat (g)
            values['646'] = 0; available['646'] = true; // Polyunsaturated Fat (g)
            values['645'] = 0; available['645'] = true; // Monounsaturated Fat (g)
        }

        // If the user entered 0 total carbs, then fiber and sugars are all zero too.
        if (nutrNo === '205' && value == 0 && available['205']) {
            values['291'] = 0; available['291'] = true; // Fiber
            values['SFB'] = 0; available['SFB'] = true; // Soluble Fiber
            values['IFB'] = 0; available['IFB'] = true; // Insoluble Fiber
            values['269'] = 0; available['269'] = true; // Sugars, total
            values['ASG'] = 0; available['ASG'] = true; // Added Sugar
        }

        if (['621', '629', '851'].includes(nutrNo) && available['621'] && available['629'] && available['851']) {
            // also update DHA+EPA and DHA+EPA+ALA
            const dha = parseFloat(values[621]) || 0;
            const epa = parseFloat(values[629]) || 0;
            const ala = parseFloat(values[851]) || 0;

            values['OM3'] = dha + epa;       // Omega-3 (DHA+EPA)
            values['OMA'] = dha + epa + ala; // Omega-3 (DHA+EPA+ALA)
            available['OMA'] = available['OM3'] = true;
        } else {
            values['OM3'] = values['OMA'] = 0;
            available['OMA'] = available['OM3'] = false;
        }

        if (['291', '205', 'SAL'].includes(nutrNo) && available['291'] && available['205']) {
            // also update net carbs
            const cho = parseFloat(values[205]) || 0;
            const fiber = parseFloat(values[291]) || 0;
            const sal = parseFloat(values['SAL']) || 0;

            values['NTC'] = cho - fiber - sal; // Net Carbs
            available['NTC'] = available[205] && available[291] && available['SAL'];
        } else {
            values['NTC'] = 0;
            available['NTC'] = false;
        }

        // Update Potential Renal Acid Load
        if (['203', '305', '306', '304', '301'].includes(nutrNo)) {

            // If phos, mag, or cal are in %dv mode, then we need to convert to absolute mode for the formula
            const pro  = parseFloat(values['203']) || 0;
            const phos = (parseFloat(values['305']) || 0) * (modes['305'] == '%' ? dailyValues[305] / 100 : 1);
            const pot  = parseFloat(values['306']) || 0;
            const mag  = (parseFloat(values['304']) || 0) * (modes['304'] == '%' ? dailyValues[304] / 100 : 1);
            const cal  = (parseFloat(values['301']) || 0) * (modes['301'] == '%' ? dailyValues[301] / 100 : 1);

            values['RAL'] = (0.49 * pro) + (0.037 * phos) - (0.021 * pot) - (0.026 * mag) - (0.013 * cal);
            available['RAL'] = true;
        }

        this.setState({values, available, errored}, this.updateParent);
    }

    showFodmapEntry = () => {
        this.setState({isFodmapEntryOpen: true});
    }

    closeFodmapEntry = () => {
        this.setState({isFodmapEntryOpen: false});
    }

    toggleNutrMode = (nutrNo) => {
        const { values, modes } = this.state;

        const parsed = parseFloat(values[nutrNo]);

        if (modes[nutrNo] === '%') {
            modes[nutrNo] = 'v';

            if (typeof parsed === 'number' && !isNaN(parsed)) {
                values[nutrNo] = (parsed / 100 * dailyValues[nutrNo]);
            }
        } else {
            modes[nutrNo] = '%';

            if (typeof parsed === 'number' && !isNaN(parsed)) {
                values[nutrNo] = (parsed / dailyValues[nutrNo] * 100);
            }
        }

        this.setState({modes, values});
    }

    renderNutrientGroup = ({nutrNos, title, fodmaps = false}, i) => {
        const { allowedNutrNos, servingUnit, servingSize, isRecipeEditor } = this.props;
        const { modes, values, available, errored } = this.state;

        if (allowedNutrNos && allowedNutrNos.length > 0) {
            nutrNos = nutrNos.filter(def => allowedNutrNos.includes(def.nutrNo) && allNutrients[def.nutrNo]);
        }

        // If no nutrients in this group will be rendered, don't render the group at all.
        if (!nutrNos.length) {
            return null; // <p>No nutrients in group {title}</p>;
        }

        return (
            <div className="nutrient-group" key={i}>
                {!isRecipeEditor ? <h3>{title}</h3> : null}

                <ul>
                    {nutrNos.map(({nutrNo, disabled}) => {
                         const unitOpts = [
                            {label: allNutrients[nutrNo].Units, value: 'v'},
                            {label: '% DV', value: '%'},
                        ];

                        const indents = {
                            "606": "1",
                            "605": "1",
                            "291": "1",
                            "269": "1",
                            "ASG": "1"
                        }
                        return (
                            <li key={nutrNo} data-indent={indents[nutrNo] || 0}>
                                <label title={allNutrients[nutrNo].NutrDesc}>
                                    <TooltipText text={allNutrients[nutrNo].NutrDesc} />
                                </label>

                                <div className="input-flex">
                                    <input type="number" disabled={disabled} placeholder="--" data-error={errored[nutrNo]} min="0"
                                        value={values[nutrNo]} data-available={available[nutrNo]}
                                        onChange={(ev) => this.onNutrientValueChange(nutrNo, ev.target.value)}
                                        onFocus={scrollTextFieldIntoView} />

                                    {isRecipeEditor && (modes[nutrNo] == '%' || dailyValues[nutrNo]) ?
                                        <span>
                                            <Select dropdownIcon="icon-down-arrow" value={modes[nutrNo] == '%' ? '%' : 'v'} options={unitOpts} onChange={() => this.toggleNutrMode(nutrNo)} />
                                        </span>
                                    : null}

                                    {!isRecipeEditor && modes[nutrNo] == '%' ?
                                        <button tabIndex="-1" onClick={() => this.toggleNutrMode(nutrNo)}
                                            title="Percent of daily recommended value contained in one serving">% DV
                                        </button>
                                    : null}

                                    {!isRecipeEditor && modes[nutrNo] !== '%' && dailyValues[nutrNo] ?
                                        <button tabIndex="-1" onClick={() => this.toggleNutrMode(nutrNo)}>{allNutrients[nutrNo].Units}</button>
                                    : null}

                                    {modes[nutrNo] !== '%' && !dailyValues[nutrNo] ?
                                        <span className="no-dropdown">{allNutrients[nutrNo].Units}</span>
                                    : null}
                                </div>
                            </li>
                        )
                    })}
                </ul>

                {fodmaps && servingUnit && servingSize ?
                    <button className="sub-action-btn" onClick={this.showFodmapEntry}>FODMAP entry tool</button>
                : null}
            </div>
        );
    }

    onCompleteFodmaps = (values, available) => {
        this.setState({values, available, isFodmapEntryOpen: false}, this.updateParent);
    }

    onGoogleSearch = () => {
        const { barcode, name } = this.props;

        if(barcode && barcode.trim().length){
            window.open(`https://www.google.com/search?q=${barcode.trim()}`, '_blank');
        } else {
            window.open(`https://www.google.com/search?q=${name.trim()}`, '_blank');
        }
    }

    renderFodmapEntryTool = () => {
        const { isFodmapEntryOpen } = this.state;
        const { food, servingUnit, servingSize } = this.props;
        const { values, available } = this.state;

        if (!isFodmapEntryOpen) {
            return;
        }

        return <FodmapEntryModal food={food}
                    closeModal={this.closeFodmapEntry}
                    servingSize={servingSize} servingUnit={servingUnit}
                    values={values} available={available}
                    onComplete={this.onCompleteFodmaps} />
    }

    render = () => {
        const { sampleSize, sampleUnit } = this.state;
        const { isSampleSizeEditable, isRecipeEditor, barcode, name, image, onShowMore, showAll} = this.props;
        const hasImage = typeof image !== 'undefined';
        const showGoogleLink = !hasImage && ((barcode && barcode.trim().length) || (name && name.trim().length));


        return (
            <section className="admin-nutrition-editor" data-recipe={isRecipeEditor}>
                {showGoogleLink ?
                    <span className="google-link-text"><p onClick={this.onGoogleSearch}>Look up nutrition facts label</p> and enter them below</span>
                : null}
                {hasImage ?
                    <p>
                        <strong>Your photos have been scanned for nutrition data.</strong>
                        <br/>
                        Please edit any missing or incorrect information below. For better results, try uploading a clearer photo of the nutrition label.
                    </p>
                : null}

                {nutrientGroups.map(this.renderNutrientGroup)}

                {!showAll && onShowMore ?
                    (<span className="show-more-toggle" onClick={onShowMore}>
                        <button className="sub-action-btn">Show more <i className="icon-chevron-down"></i></button>
                    </span>)
                : null}

                {this.renderFodmapEntryTool()}
            </section>
        );
    }
}
